import { useState, useEffect, useMemo, useCallback } from "react";
import { createAppResourceService } from "@/global";
import { useNotify } from "@imminently/react/store/notifications";
import { ClinicianIdRecord } from "../components/schemas";

const CLINICIAN_IDS_ID = "trier";

export type ClinicianIdsPayload = {
  id: string;
  clinicianIds: ClinicianIdRecord[];
  fallbackClinicianId: string;
};

const cliniciansService = createAppResourceService<ClinicianIdsPayload>("meta/clinicians");

type UseClinicianIdsReturn = {
  entries: ClinicianIdRecord[];
  setEntries: React.Dispatch<React.SetStateAction<ClinicianIdRecord[]>>;
  fallbackId: string;
  setFallbackId: React.Dispatch<React.SetStateAction<string>>;
  isDirty: boolean;
  isInitialized: boolean;
  isSaving: boolean;
  isLoading: boolean;
  save: () => Promise<void>;
  reset: () => void;
};

/**
 * Hook for managing clinician IDs and fallback from the dedicated meta/clinicians route.
 * Replaces useSettingsState for the Clinician IDs settings page.
 */
export const useClinicianIds = (): UseClinicianIdsReturn => {
  const notify = useNotify();
  const { data, isLoading: isLoadingQuery, refetch } = cliniciansService.useGetOne(CLINICIAN_IDS_ID, {
    refetchOnWindowFocus: false,
  });
  const { mutateAsync: updateClinicianIds, isLoading: isSaving } = cliniciansService.useUpdate();

  const originalPayload = useMemo((): ClinicianIdsPayload => {
    const id = typeof data?.id === "string" ? data.id : CLINICIAN_IDS_ID;
    const ids = Array.isArray(data?.clinicianIds) ? data.clinicianIds : [];
    const fallback = typeof data?.fallbackClinicianId === "string" ? data.fallbackClinicianId : "";
    return { id, clinicianIds: ids, fallbackClinicianId: fallback };
  }, [data]);

  const [entries, setEntries] = useState<ClinicianIdRecord[]>(originalPayload.clinicianIds);
  const [fallbackId, setFallbackId] = useState<string>(originalPayload.fallbackClinicianId);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setEntries(originalPayload.clinicianIds);
    setFallbackId(originalPayload.fallbackClinicianId);
    setIsInitialized(true);
  }, [originalPayload.clinicianIds, originalPayload.fallbackClinicianId]);

  const isDirty = useMemo(() => {
    if (!isInitialized) return false;
    const entriesMatch =
      JSON.stringify(entries) === JSON.stringify(originalPayload.clinicianIds);
    const fallbackMatch = fallbackId === originalPayload.fallbackClinicianId;
    return !entriesMatch || !fallbackMatch;
  }, [entries, fallbackId, originalPayload, isInitialized]);

  const save = useCallback(async () => {
    try {
      const sanitizedEntries = entries.map((entry) => {
        if (entry.operatorCode === "*") {
          const { operatorCode, ...rest } = entry;
          return rest;
        }
        return entry;
      });
      await updateClinicianIds({
        id: CLINICIAN_IDS_ID,
        payload: { clinicianIds: sanitizedEntries, fallbackClinicianId: fallbackId },
      });
      await refetch();
      notify.success("Clinician IDs updated successfully.");
    } catch (err) {
      console.error("Failed to update clinician IDs:", err);
      const backendMessage = (err as any)?.response?.data?.message;
      const fallbackMessage =
        (err as any)?.message
        ?? "Failed to update clinician IDs. Please try again.";
      notify.error(backendMessage || fallbackMessage);
    }
  }, [entries, fallbackId, updateClinicianIds, refetch, notify]);

  const reset = useCallback(() => {
    setEntries(originalPayload.clinicianIds);
    setFallbackId(originalPayload.fallbackClinicianId);
  }, [originalPayload.clinicianIds, originalPayload.fallbackClinicianId]);

  return {
    entries,
    setEntries,
    fallbackId,
    setFallbackId,
    isDirty,
    isInitialized,
    isSaving,
    isLoading: isLoadingQuery,
    save,
    reset,
  };
};
